import type { ExecuteContext } from '@cotera/client/v0/actions/framework';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { WorkflowRunsModel } from './workflow-runs.model';
import { WorkflowRunModel } from './workflow-run.model';
import { WorkflowRunActionTargetsModel } from './workflow-run-action-targets.model';

export type WorkflowRunActionErrors = { t: string; message?: string }[];

export type WorkflowRunActionsClient = Pick<TenantedClient, 'resource'>;

/** `1 run` / `3 runs`. */
export const runNoun = (count: number): string =>
  `${count} ${count === 1 ? 'run' : 'runs'}`;

export function workflowRunTargetsFromContext(
  context: Pick<ExecuteContext, 'getInScopeModelOfType'>
): WorkflowRunActionTargetsModel | null {
  return context.getInScopeModelOfType(WorkflowRunActionTargetsModel);
}

/**
 * Refetches whichever run views are in scope. The server broadcasts
 * `ResourceUpdated` after a run mutation, but that round-trips through
 * pg_notify and a debounce; refreshing directly makes the change land
 * immediately.
 */
export function refreshWorkflowRunViews(
  context: Pick<ExecuteContext, 'getInScopeModelOfType'>
): void {
  context.getInScopeModelOfType(WorkflowRunsModel)?.refresh();
  context.getInScopeModelOfType(WorkflowRunModel)?.refresh();
}

/**
 * Aggregate toast copy for a batch: one message for the whole selection rather
 * than one per run.
 */
export function reportWorkflowRunBatch(params: {
  pending: {
    success: (message: string) => void;
    error: (message: string) => void;
  };
  total: number;
  succeeded: number;
  allSucceededMessage: (noun: string) => string;
  noneSucceededMessage: (noun: string) => string;
  partialMessage: (succeeded: number, noun: string) => string;
}): void {
  const noun = runNoun(params.total);
  if (params.succeeded === params.total) {
    params.pending.success(params.allSucceededMessage(noun));
    return;
  }
  if (params.succeeded === 0) {
    params.pending.error(params.noneSucceededMessage(noun));
    return;
  }
  params.pending.error(params.partialMessage(params.succeeded, noun));
}
